import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
  AfterViewInit,
  AfterViewChecked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { myCustomTheme } from '../../../constants/grid-themes';
import { HubNotification } from '@models/notification.model';
import { NotificationsAdminActions } from '@store/actions/notifications/notifications.actions';
import {
  selectFilteredNotifications,
  selectNotificationsLoading,
  selectShowInactive,
} from '@store/selectors/notifications/notifications.selectors';
import { selectCanManageNotifications } from '@store/selectors/user/user.selectors';
import { selectOrganizations } from '@store/selectors/organization-admin/organization-admin.selector';
import { OrganizationAdminActions } from '@store/actions/organization-admin/organization-admin.actions';
import { ConfirmationModalComponent } from '../../confirmation-modal/confirmation-modal.component';
import { NotificationModalComponent } from '../../modals/notification-modal/notification-modal.component';
import { NotificationsActionsRendererComponent } from './notifications-actions-renderer.component';

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-notifications-admin',
  standalone: true,
  imports: [CommonModule, AgGridAngular, MatButtonModule, MatCheckboxModule, MatIconModule],
  templateUrl: './notifications-admin.component.html',
  styleUrl: './notifications-admin.component.css',
})
export class NotificationsAdminComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly destroy$ = new Subject<void>();

  theme = myCustomTheme;
  private gridApi: any;
  private orgNameMap = new Map<number, string>();
  notifications: HubNotification[] = [];
  isLoading = true;
  showInactive = false;

  rowData$ = this.store.select(selectFilteredNotifications);
  loading$ = this.store.select(selectNotificationsLoading);
  showInactive$ = this.store.select(selectShowInactive);
  canManage$ = this.store.select(selectCanManageNotifications);

  public gridOptions: GridOptions = {
    rowSelection: 'single',
    pagination: true,
    paginationPageSize: 20,
    suppressClickEdit: true,
    loading: true,
    rowData: [],
    onGridReady: this.onGridReady.bind(this),
    getRowStyle: (params: any) => {
      if (params.data && !params.data.isActive) {
        return { color: '#999', fontStyle: 'italic' };
      }
      return undefined;
    },
  };

  colDefs: ColDef[] = [
    {
      field: 'title',
      headerName: 'Notification',
      flex: 2,
      tooltipValueGetter: (p: any) => p.value,
    },
        {
      field: 'organizationIds',
      headerName: 'Organization(s)',
      flex: 2,
      valueGetter: (p: any) => {
        const ids: number[] = p.data?.organizationIds ?? [];
        if (ids.length === 0) return 'All';
        return ids.map((id: number) => this.orgNameMap.get(id) ?? `Org ${id}`).join(', ');
      },
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      field: 'createdDate',
      headerName: 'Date',
      flex: 1,
      valueFormatter: (p: any) => {
        if (!p.value) return '';
        const [year, month, day] = (p.value as string).split('T')[0].split('-').map(Number);
        const d = new Date(year, month - 1, day);
        return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      flex: 1,
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      minWidth: 160,
      width: 160,
      pinned: 'right',
      filter: false,
      sortable: false,
      resizable: false,
      cellRenderer: NotificationsActionsRendererComponent,
    },
  ];

  defaultColDef: ColDef = {
    filter: true,
    resizable: true,
    sortable: true,
    editable: false,
    minWidth: 100,
  };

  ngOnInit(): void {
    this.showInactive$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.showInactive = val;
    });

    this.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.isLoading = loading;
      if (this.gridApi) {
        this.gridApi.setGridOption('loading', loading);
      }
    });

    this.rowData$.pipe(takeUntil(this.destroy$)).subscribe((notifications) => {
      this.notifications = notifications || [];
      if (this.gridApi && !this.isLoading) {
        this.gridApi.setGridOption('rowData', this.notifications);
      }
    });

    this.store.dispatch(NotificationsAdminActions.loadNotifications({ showInactive: this.showInactive }));

    this.store.select(selectOrganizations).pipe(take(1)).subscribe((orgs) => {
      if (orgs.length === 0) {
        this.store.dispatch(OrganizationAdminActions.loadOrganizations());
      }
    });

    this.store.select(selectOrganizations).pipe(takeUntil(this.destroy$)).subscribe((orgs) => {
      this.orgNameMap = new Map(orgs.map((org) => [Number(org.id), org.name]));
      if (this.gridApi) {
        this.gridApi.refreshCells({ columns: ['organizationIds'] });
      }
    });
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setGridOption('loading', this.isLoading);
    if (this.notifications.length > 0) {
      this.gridApi.setGridOption('rowData', this.notifications);
    }
  }

  onCellClicked(params: any): void {
    if (params.column.colId !== 'actions') return;
    const action = params.event?.target?.dataset?.action;
    if (!action) return;
    const data = params.data as HubNotification;

    if (action === 'view') {
      this.openModal(data, 'view');
    } else if (action === 'edit') {
      this.openModal(data, 'edit');
    } else if (action === 'delete') {
      this.confirmDelete(data);
    } else if (action === 'reactivate') {
      this.store.dispatch(NotificationsAdminActions.reactivateNotification({ id: data.id }));
    }
  }

  openAddNewModal(): void {
    this.openModal(undefined, 'create');
  }

  onShowInactiveChange(checked: boolean): void {
    this.store.dispatch(NotificationsAdminActions.setShowInactive({ showInactive: checked }));
    this.store.dispatch(NotificationsAdminActions.loadNotifications({ showInactive: checked }));
  }

  private openModal(notification: HubNotification | undefined, mode: 'create' | 'edit' | 'view'): void {
    this.dialog.open(NotificationModalComponent, {
      width: '560px',
      disableClose: true,
      data: { notification, mode },
    });
  }

  private confirmDelete(data: HubNotification): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: 'Delete Notification',
        content: `Are you sure you want to delete "${data.title}"?`,
        confirmText: 'Delete',
        cancelText: 'Close',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.store.dispatch(NotificationsAdminActions.deleteNotification({ id: data.id }));
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
